import { Injectable, inject } from '@angular/core';
import { LayerEntity, LayerTypeEnum } from '@gishub/domain';
import Layer from '@arcgis/core/layers/Layer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import Color from '@arcgis/core/Color';

import { LayerSymbolFactory, LayerStyleConfig } from './layer-symbol-factory';
import {Configuration} from '../../shared/configuration/configuration';

@Injectable({ providedIn: 'root' })
export class ArcGISLayerFactory {
  private readonly _symbolFactory = inject(LayerSymbolFactory);
  private readonly _config = inject(Configuration);
  private readonly _styles = new Map<string, LayerStyleConfig>();

  private get cleanProxyUrl(): string | null {
    return this._config.proxyUrl
      ? this._config.proxyUrl.replace(/\?.*$/, '').replace(/\/+$/, '')
      : null;
  }

  create(entity: LayerEntity): Layer | null {
    if (!entity.url) return null;

    switch (entity.type) {
      case LayerTypeEnum.MapServer:
        return this.createMapImageLayer(entity);
      case LayerTypeEnum.FeatureServer:
        return this.createFeatureLayer(entity);
      case LayerTypeEnum.GeoJson:
        return this.createGeoJSONLayer(entity);
      case LayerTypeEnum.VectorTileServer:
        return this.createVectorTileLayer(entity);
      default:
        return null;
    }
  }

  registerStyle(name: string, style: LayerStyleConfig): void {
    this._styles.set(name, style);
  }

  unregisterStyle(name: string): void {
    this._styles.delete(name);
  }

  private createFeatureLayer(entity: LayerEntity): FeatureLayer | null {
    const { url, sublayerId } = this._parseServiceUrl(entity.url!);
    const layer: FeatureLayer | any = new FeatureLayer({
      url,
      layerId: sublayerId ?? undefined,
      title: entity.name,
      id: entity.name,
      visible: entity.visible,
      definitionExpression: entity.definitionQuery || undefined,
      outFields: ['*'],
      opacity: entity.opacity,
      popupEnabled: entity.popupEnabled,
      labelsVisible: entity.showLabels,
    });
    layer.layerType = LayerTypeEnum.FeatureServer;
    layer.proxyUrl = entity.hasProxy ? this.cleanProxyUrl : null;
    layer.config = entity;
    // Aplicar renderer personalizado para capas de límites administrativos
    if (entity.name === 'Límite departamental' || entity.name === 'Límite provincial' || entity.name === 'Límite distrital') {
      layer.when(() => this._applyBoundaryRenderer(layer, entity.name));
    }
    return layer;
  }

  private createMapImageLayer(entity: LayerEntity): MapImageLayer | null {
    if (!entity.url) return null;
    const { url, sublayerId } = this._parseServiceUrl(entity.url);

    const layer: MapImageLayer | any = new MapImageLayer({
      url,
      title: entity.name,
      id: entity.name,
      visible: entity.visible,
      opacity: entity.opacity,
    });
    layer.layerType = LayerTypeEnum.MapServer;
    layer.proxyUrl = entity.hasProxy ? this.cleanProxyUrl : null;
    layer.config = entity;
    if (sublayerId !== null) {
      layer.when(() => {
        const target = layer.findSublayerById(sublayerId);
        if (target) {
          // asegurar que todos los ancestros sean visibles
          let ancestor = target.parent as any;
          while (ancestor) {
            ancestor.visible = true;
            ancestor = ancestor.parent;
          }
          // ocultar solo hermanos del mismo padre, no todo el árbol
          const container = (target.parent as any)?.sublayers ?? layer.sublayers;
          container?.forEach((s: any) => { s.visible = false; });
          target.visible = true;
          target.layerType = LayerTypeEnum.SubServer;
          target.proxyUrl = entity.hasProxy ? this.cleanProxyUrl : null;
          layer.config = entity;
          target.popupEnabled = entity.popupEnabled;
          target.labelsVisible = entity.showLabels;
          if (entity.definitionQuery) {
            target.definitionExpression = entity.definitionQuery;
          }
          this._applyBoundaryRenderer(target, entity.name);
        }
      });
    }

    return layer;
  }

  private createGeoJSONLayer(entity: LayerEntity): GeoJSONLayer | null {
    if (!entity.url) return null;
    const layer: GeoJSONLayer | any = new GeoJSONLayer({
      url: entity.url,
      title: entity.name,
      id: entity.name,
      visible: entity.visible,
      definitionExpression: entity.definitionQuery || undefined,
      opacity: entity.opacity,
      popupEnabled: entity.popupEnabled,
    });
    layer.layerType = LayerTypeEnum.GeoJson;
    layer.proxyUrl = entity.hasProxy ? this.cleanProxyUrl : null;
    layer.config = entity;
    const style = this._styles.get(entity.name);
    if (style) {
      layer.when(
        () => {
          if (layer.geometryType) {
            layer.renderer = this._symbolFactory.createRenderer(layer.geometryType, style);
          }
        },
        (err: any) => console.error('[ArcGISLayerFactory] GeoJSONLayer error:', err)
      );
    }
    return layer;
  }

  private createVectorTileLayer(entity: LayerEntity): VectorTileLayer | null {
    if (!entity.url) return null;
    const layer: VectorTileLayer | any = new VectorTileLayer({
      url: entity.url,
      title: entity.name,
      id: entity.name,
      visible: entity.visible,
      opacity: entity.opacity,
    });
    layer.layerType = LayerTypeEnum.VectorTileServer;
    layer.proxyUrl = entity.hasProxy ? this.cleanProxyUrl : null;
    layer.config = entity;
    return layer;
  }

  private _applyBoundaryRenderer(sublayer: any, name: string): void {
    if (name === 'Límite departamental') {
      sublayer.renderer = new SimpleRenderer({
        symbol: new SimpleFillSymbol({
          color: new Color([173, 216, 230, 0.08]), // celeste muy tenue
          outline: new SimpleLineSymbol({
            color: new Color([0, 180, 255, 1]), // celeste/azul brillante
            width: 2.5,
            style: 'solid',
          }),
        }),
        label: 'Departamento',
      });
    } else if (name === 'Límite provincial') {
      sublayer.renderer = new SimpleRenderer({
        symbol: new SimpleFillSymbol({
          color: null as any,
          outline: new SimpleLineSymbol({
            color: new Color([0, 0, 0, 1]),
            width: 2,
            style: 'solid',
          }),
        }),
        label: 'Provincia',
      });
    } else if (name === 'Límite distrital') {
      sublayer.renderer = new SimpleRenderer({
        symbol: new SimpleFillSymbol({
          color: null as any,
          outline: new SimpleLineSymbol({
            color: new Color([120, 120, 120, 1]),
            width: 1.75,
            style: 'solid',
          }),
        }),
        label: 'Distrito',
      });
    }
  }

  private _parseServiceUrl(serviceUrl: string): { url: string; sublayerId: number | null } {
    const match = serviceUrl.match(/^(.+?\/(?:MapServer|FeatureServer))\/(\d+)$/i);
    if (match) {
      return { url: match[1], sublayerId: parseInt(match[2], 10) };
    }
    return { url: serviceUrl, sublayerId: null };
  }
}
